import { useState, useEffect } from 'react';

import { Card } from '@common/ui/Card';
import { Cell, type CellProps } from '@common/ui/Cell';
import { Checkbox } from '@common/ui/Checkbox';
import { Separator } from '@common/ui/Separator';
import { Heading } from '@common/ui/typography/Heading';

export interface Item {
  value: string;
  label: string;
  group?: string;
}

export interface CellMultiSelectProps
  extends Omit<
    CellProps,
    'suffixIcon' | 'onChange' | 'title' | 'subtitle' | 'testId'
  > {
  items: Item[];
  value?: string[];
  onChange?: (value: string[]) => void;
}

export const CellMultiSelect = ({
  items,
  value,
  onChange,
}: CellMultiSelectProps) => {
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    if (value !== undefined) {
      setSelected(value);
    } else {
      setSelected([]);
    }
  }, [value]);

  const onChangeHandler = (value: string) => {
    let newValue = selected;

    if (selected.includes(value)) {
      newValue = newValue.filter((item) => item !== value);
    } else {
      newValue = [...selected, value];
    }

    setSelected(newValue);
    onChange?.(newValue);
  };

  return (
    <>
      {Array.from(new Set(items.map((item) => item.group))).map(
        (group, groupIndex) => {
          const groupItems = items.filter((item) => item.group === group);

          return (
            <Card
              key={groupIndex}
              testId={`cell-multi-select-group-${groupIndex}`}
              style={{ paddingBottom: 0 }}
            >
              <Heading level={4}>{group}</Heading>

              {groupItems.map((item, index) => (
                <Cell
                  key={item.value}
                  testId={`cell-multi-select-option-${item.value}`}
                  title={item.label}
                  separator={
                    index < groupItems.length - 1 ? <Separator /> : undefined
                  }
                  suffixIcon={
                    <Checkbox
                      checked={selected.includes(item.value)}
                      testId={`cell-multi-select-checkbox-${item.value}`}
                      onClick={() => onChangeHandler(item.value)}
                    />
                  }
                />
              ))}
            </Card>
          );
        },
      )}
    </>
  );
};
